// Net: one WebSocket, seq-checked patches, resync, in-memory session token
// resume with backoff, server-clock offset. Implements PROTOCOL.md
// "Envelope" + "Sessions & liveness".

import * as vsj from './vsj';
import type { Fx } from './types';

const DEFAULT_URL = (() => {
  const q = new URLSearchParams(location.search).get('ws');
  if (q) return q;
  if (location.protocol.startsWith('http') && location.host)
    return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
  return 'ws://127.0.0.1:8137/ws';
})();

let ws: WebSocket | null = null;
let lastSeq = 0;
let token: string | null = null; // memory only, never persisted
let clockOffset = 0; // serverNow - performance-now-based local ms
let resyncPending = false;
let backoffMs = 500;
let everConnected = false;

export let connected = false;

type FxHandler = (fx: Fx[]) => void;
let fxHandler: FxHandler = () => {};
export function onFx(fn: FxHandler): void {
  fxHandler = fn;
}
let statusHandler: (s: string) => void = () => {};
export function onStatus(fn: (s: string) => void): void {
  statusHandler = fn;
}

/** Server-time now (ms). Progress bars target ends_at minus ~100ms lead. */
export function serverNow(): number {
  return Date.now() + clockOffset;
}

export function connect(): void {
  statusHandler(everConnected ? 'Reconnecting…' : 'Connecting…');
  ws = new WebSocket(DEFAULT_URL);
  ws.onopen = () => {
    connected = true;
    backoffMs = 500;
    lastSeq = 0;
    resyncPending = false;
    statusHandler('');
    if (token) rawSend({ _event_id: 'resume', token });
    everConnected = true;
  };
  ws.onmessage = (ev) => handleFrame(String(ev.data));
  ws.onclose = (ev) => {
    connected = false;
    ws = null;
    if (ev.code === 4000) token = null; // logged in elsewhere
    statusHandler(ev.code === 4000 ? 'Logged in elsewhere.' : 'Disconnected — retrying…');
    setTimeout(connect, backoffMs);
    backoffMs = Math.min(backoffMs * 2, 8000);
  };
  ws.onerror = () => ws?.close();
}

function handleFrame(text: string): void {
  let msg: any;
  try {
    msg = JSON.parse(text);
  } catch {
    return;
  }
  switch (msg.type) {
    case 'vsj':
      lastSeq = msg.seq ?? 0;
      resyncPending = false;
      if (typeof msg.now === 'number') clockOffset = msg.now - Date.now();
      vsj.setFull(msg.vsj ?? {});
      break;
    case 'patch': {
      if (typeof msg.now === 'number') clockOffset = msg.now - Date.now();
      if (msg.seq !== lastSeq + 1) {
        requestResync();
        return;
      }
      lastSeq = msg.seq;
      if (!vsj.applyPatch(msg.patch ?? [])) {
        requestResync();
        return;
      }
      if (Array.isArray(msg.fx) && msg.fx.length) fxHandler(msg.fx);
      break;
    }
    case 'session':
      token = msg.token ?? null;
      break;
  }
}

function requestResync(): void {
  if (resyncPending) return;
  resyncPending = true;
  rawSend({ _event_id: 'resync' });
}

function rawSend(obj: Record<string, unknown>): void {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
}

/** Send a named event with args. */
export function sendEvent(eventId: string, args: Record<string, unknown> = {}): void {
  rawSend({ _event_id: eventId, ...args });
}

/** Fire a vsj {btn}: _req_args are DEFAULTS, overrides win. */
export function sendBtn(
  btn: { _event_id: string; _req_args?: Record<string, unknown> } | null | undefined,
  overrides: Record<string, unknown> = {},
): void {
  if (!btn) return;
  rawSend({ _event_id: btn._event_id, ...(btn._req_args ?? {}), ...overrides });
}
